/**
 * OpenAI function-calling tool definitions for the AI Maths Tutor.
 * Three categories:
 *   • plot_graph          (functions of x)
 *   • geometry (4 shape-specific tools)
 *   • draw_data_chart     (bar / pie / histogram / scatter / box / line)
 */

export const openAITools = [
  /* ────────────── GRAPHING ────────────── */
  {
    type: "function",
    function: {
      name: "plot_graph",
      description:
        "Plots a continuous single-variable function, e.g. y = x² or f(x) = sin(x).",
      parameters: {
        type: "object",
        properties: {
          expression: { type: "string", description: 'e.g. "x^2 - 3x + 2"' },
          xRange: {
            type: "array",
            items: { type: "number" },
            description: "Optional x-axis range [min,max]. Default [-10,10].",
            default: [-10, 10],
          },
          yRange: {
            type: "array",
            items: { type: "number" },
            description: "Optional y-axis range [min,max].",
          },
          title: { type: "string", description: "Optional graph title." },
          gridLines: {
            type: "boolean",
            description: "Show grid? Default true.",
            default: true,
          },
        },
        required: ["expression"],
      },
    },
  },

  /* ───────────── GEOMETRY – shape-specific ───────────── */

  // 1. Circle
  {
    type: "function",
    function: {
      name: "draw_circle",
      description: "Draws a circle with optional radius/diameter lines and labels.",
      parameters: {
        type: "object",
        properties: {
          radius: { type: "number", description: "Circle radius.", default: 1 },
          show_radius_line: { 
            type: "boolean", 
            default: false,
            description: "Draw a radius line from center to edge"
          },
          show_diameter_line: { 
            type: "boolean", 
            default: false,
            description: "Draw a diameter line across the circle" 
          },
          center_label: { type: "string" },
          radius_label: { type: "string" },
          diameter_label: { type: "string" },
          circumference_label: { type: "string" },
          title: { type: "string" },
        },
        required: ["radius"],
      },
    },
  },

  // 2. Triangle
  {
    type: "function",
    function: {
      name: "draw_triangle",
      description:
        "Draws a labelled triangle. If no dimensions given, defaults to an equilateral triangle.",
      parameters: {
        type: "object",
        properties: {
          base: {
            type: "number",
            description: "Base length of the triangle in units",
            default: 5,
          },
          height: {
            type: "number",
            description: "Height of the triangle in units",
            default: 3,
          },

          /* Existing geometry properties */
          side_lengths: {
            description:
              "Either [a,b,c] or {AB,BC,CA}. Optional (defaults to equilateral).",
            oneOf: [
              { type: "array", minItems: 3, maxItems: 3, items: { type: "number" } },
              { type: "object" },
            ],
          },
          angle_measures: {
            description: "Either [A,B,C] degrees or {A,B,C}. Optional.",
            oneOf: [
              { type: "array", minItems: 3, maxItems: 3, items: { type: "number" } },
              { type: "object" },
            ],
          },
          show_altitude_from: { type: "string", enum: ["A", "B", "C"] },

          /* Labels (all optional) */
          vertex_A_label: { type: "string" },
          vertex_B_label: { type: "string" },
          vertex_C_label: { type: "string" },
          side_AB_label: { type: "string" },
          side_BC_label: { type: "string" },
          side_CA_label: { type: "string" },
          angle_A_label: { type: "string" },
          angle_B_label: { type: "string" },
          angle_C_label: { type: "string" },

          title: { type: "string" },
        },
        required: [], // Keep empty since you have defaults
      },
    },
  },

  // 3. Quadrilateral family
  {
    type: "function",
    function: {
      name: "draw_quadrilateral",
      description:
        "Draws a square, rectangle, parallelogram or trapezoid with optional labels.",
      parameters: {
        type: "object",
        properties: {
          type: {
            type: "string",
            enum: ["square", "rectangle", "parallelogram", "trapezoid"],
          },
          
          // For squares
          side_length: { 
            type: "number", 
            description: "Side length for squares" 
          },
          
          // For rectangles  
          base: { 
            type: "number", 
            description: "Base/length for rectangles, parallelograms, trapezoids" 
          },
          height: { 
            type: "number", 
            description: "Height for rectangles, parallelograms, trapezoids" 
          },
          
          // For parallelograms
          slant: { 
            type: "number", 
            description: "Slant offset for parallelograms",
            default: 1.5
          },
          
          // For trapezoids
          base2: { 
            type: "number", 
            description: "Second base length for trapezoids" 
          },
          
          vertex_labels: {
            type: "array",
            items: { type: "string" },
            description: "Labels for vertices in order (A, B, C, D).",
          },
          side_labels: {
            type: "array",
            items: { type: "string" },
            maxItems: 4,
            description: "Labels for sides in order (AB, BC, CD, DA). Optional."
          },
          title: { type: "string" },
        },
        required: ["type"],
      },
    },
  },

  // 4. Regular polygon
  {
    type: "function",
    function: {
      name: "draw_polygon_regular",
      description: "Draws a regular n-gon (5 ≤ n ≤ 10) with specified radius.",
      parameters: {
        type: "object",
        properties: {
          num_sides: { type: "integer", minimum: 5, maximum: 10 },
          side_length: { 
            type: "number", 
            default: 3,
            description: "Radius of the polygon (distance from center to vertex)"
          },
          center_label: { type: "string" },
          vertex_labels: {
            type: "array",
            items: { type: "string" },
            description: "Labels for each vertex, clockwise from the top.",
          },
          side_labels: {
            type: "array",
            items: { type: "string" },
            description: "Labels for each side between consecutive vertices. Optional."
          },
          title: { type: "string" },
        },
        required: ["num_sides"],
      },
    },
  },
];